import { inject, Injectable } from '@angular/core';
import { RootStore } from '@sama/app-state';
import { keyBy, minBy } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { ObjectClassService } from '../../../../../../../core/shared/misc-services/object-class.service';
import { isClassTaxonomy } from '../../../../../../../core/shared/misc-services/taxonomy-loading.service';
import { defaultAnnotationColor } from '../../../../../../../cross-team-shared/constants';
import { getOrThrowStore } from '../../../../../../../cross-team-shared/misc-utils/assert-store';
import {
  getOrThrow,
  strictValues,
} from '../../../../../../../cross-team-shared/misc-utils/strict-record';
import { toggleState } from '../../../../../../../shared/misc-utilities/store-utils';
import { TaxonomyItem } from '../../../../../../../team-apis/core/taxonomy';

export interface ClassSelectorTreeNode {
  id: string;
  label: string;
  name?: string;
  isExpanded: boolean;
  isSelected: boolean;
  isFilteredOut: boolean;
  parentId?: string;
  childIds: string[];
  level: number; // 0 based
  color: string;
}

@Injectable()
export class ClassSelectorService {
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  private readonly objectClassService = inject(ObjectClassService);
  private readonly store = new RootStore(this.buildInitialNodes());

  highlightedNodeId$ = new BehaviorSubject(
    this.getFirstClassClosestToRoot()?.id,
  );

  getNodes$(parentId?: string) {
    return this.store.$.pipe(map(() => this.getNodes(parentId)));
  }

  getNodes(parentId?: string) {
    return strictValues(this.store.state())
      .filter((node) => node.parentId === parentId)
      .filter((node) => !node.isFilteredOut);
  }

  getAllNodes() {
    return strictValues(this.store.state());
  }

  toggleIsExpanded(id: string) {
    const isExpandedStore = getOrThrowStore(this.store, id)('isExpanded');
    toggleState(isExpandedStore);
    return isExpandedStore.state();
  }

  filterNodes(filter: string) {
    const filterText = filter.toLowerCase().trim();

    const matchingNodeIds = new Set(
      strictValues(this.store.state())
        .filter((node) => node.label.toLowerCase().includes(filterText))
        .map((node) => node.id),
    );

    this.store.batch(() => {
      strictValues(this.store.state()).forEach((node) => {
        const isFilteredOut =
          !matchingNodeIds.has(node.id) &&
          !hasParent(node, this.store.state(), matchingNodeIds) &&
          !hasChild(node, this.store.state(), matchingNodeIds);
        getOrThrowStore(
          this.store,
          node.id,
        )('isFilteredOut').set(isFilteredOut);
      });
      this.updateHighlight();
    });
  }

  selectClass(className?: string) {
    const path = new Set(
      this.objectClassService.getClass(className)?.path ?? [],
    );
    this.store.batch(() => {
      this.collapseAll();
      Object.keys(this.store.state()).forEach((id) => {
        getOrThrowStore(this.store, id)('isSelected').set(path.has(id));
      });
      className && this.expandClass(className);
      this.updateHighlight();
    });
  }

  updateHighlight() {
    const nodes = this.store.state();

    if (
      strictValues(nodes).some(
        (node) => isClassNode(node) && node.isSelected && !node.isFilteredOut,
      )
    ) {
      this.highlightedNodeId$.next(undefined);
      return;
    }

    this.highlightedNodeId$.next(this.getNodeToHighlight()?.id);
    this.highlightedNodeId$.value &&
      this.expandParents(this.highlightedNodeId$.value);
  }

  getHighlightedClass() {
    return this.highlightedNodeId$.value
      ? this.store.state()[this.highlightedNodeId$.value]?.name
      : undefined;
  }

  private getNodeToHighlight() {
    const { nextObjectClass } = this.taskWorkspaceStore.state();
    const defaultClassNode = this.getFirstClassClosestToRoot();

    if (!nextObjectClass) {
      return defaultClassNode;
    }

    const lastSelectedClassNode = strictValues(this.store.state()).find(
      (node) => node.name === nextObjectClass,
    );

    return !lastSelectedClassNode || lastSelectedClassNode.isFilteredOut
      ? defaultClassNode
      : lastSelectedClassNode;
  }

  private expandClass(className: string) {
    const path = this.objectClassService.getClass(className)?.path;
    this.store.batch(() => {
      path?.forEach((id) =>
        getOrThrowStore(this.store, id)('isExpanded').set(true),
      );
    });
  }

  private collapseAll() {
    this.store.batch(() => {
      Object.entries(this.store.state())
        .filter(([_, node]) => !!node?.parentId)
        .forEach(([id]) => {
          getOrThrowStore(this.store, id)('isExpanded').set(false);
        });
    });
  }

  private buildInitialNodes(): StrictRecord<string, ClassSelectorTreeNode> {
    const { objectTaxonomy } = this.taskWorkspaceStore.state().taxonomy;
    const nodes: ClassSelectorTreeNode[] = [];
    objectTaxonomy.forEach((item) => addNode(item, nodes));
    return keyBy(nodes, 'id');
  }

  private getFirstClassClosestToRoot() {
    const nodes = this.store.state();
    return minBy(
      strictValues(nodes).filter(
        (node) => isClassNode(node) && !node.isFilteredOut,
      ),
      (node) => node.level,
    );
  }

  private expandParents(id: string) {
    let parentId = getOrThrow(this.store.state(), id).parentId;
    this.store.batch(() => {
      while (parentId) {
        getOrThrowStore(this.store, parentId)('isExpanded').set(true);
        parentId = getOrThrow(this.store.state(), parentId).parentId;
      }
    });
  }
}

function addNode(
  item: TaxonomyItem,
  nodes: ClassSelectorTreeNode[],
  parent?: ClassSelectorTreeNode,
) {
  const childIds = isClassTaxonomy(item)
    ? []
    : item.children.map((child) => child.__id);
  const node: ClassSelectorTreeNode = {
    id: item.__id,
    label: item.label,
    isExpanded: !parent,
    isSelected: false,
    isFilteredOut: false,
    childIds,
    parentId: parent?.id,
    level: parent ? parent.level + 1 : 0,
    color: item.color || defaultAnnotationColor,
  };
  nodes.push(node);

  if (isClassTaxonomy(item)) {
    node.name = item.name;
    return;
  }

  item.children.forEach((child) => addNode(child, nodes, node));
}

function hasParent(
  node: ClassSelectorTreeNode,
  nodes: StrictRecord<string, ClassSelectorTreeNode>,
  parentNodeIds: Set<string>,
): boolean {
  const parent = node.parentId ? nodes[node.parentId] : undefined;

  if (!parent) {
    return false;
  }

  return (
    parentNodeIds.has(parent.id) || hasParent(parent, nodes, parentNodeIds)
  );
}

function hasChild(
  node: ClassSelectorTreeNode,
  nodes: StrictRecord<string, ClassSelectorTreeNode>,
  childNodeIds: Set<string>,
): boolean {
  return node.childIds.some((childId) => {
    const child = getOrThrow(nodes, childId);
    return childNodeIds.has(child.id) || hasChild(child, nodes, childNodeIds);
  });
}

export function isClassNode(node: ClassSelectorTreeNode) {
  return !!node.name;
}
